import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Product } from "./types/products";
import {
  useKivunovaTranslation,
  PORTAL_PREFIX,
  PAGE_PORTAL_INVENTORY_PRODUCT
} from "@kivunova/kivufrontendcommon";
import { useDeleteProduct } from "../../hooks/api/useProduct";
import ConfirmActionDialog from "../common/GuidanceDialog/ConfirmActionDialog";

interface DeleteProductModalProps {
  open: boolean;
  onClose: () => void;
  product: Product;
}

const DeleteProductModal: React.FC<DeleteProductModalProps> = ({
  open,
  onClose,
  product
}) => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const deleteProduct = useDeleteProduct();

  const busy = deleteProduct.isPending;

  const handleDelete = async () => {
    try {
      const ok = await deleteProduct.mutateAsync({ id: product.id });
      if (ok) {
        onClose();
        navigate(`/${PORTAL_PREFIX}/${PAGE_PORTAL_INVENTORY_PRODUCT}`);
      }
    } catch {
      // error already handled inside useDeleteProduct
    }
  };

  return (
    <ConfirmActionDialog
      open={open}
      onClose={onClose}
      onConfirm={handleDelete}
      loading={busy}
      dataTestId="delete-product-modal"
      title={t(
        "delete-product.confirm-title",
        "Do you really want to delete this product?"
      )}
      description={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "delete-product.confirm-require-items-cleared",
              "Every product item must be sold or removed from inventory before you can delete the product."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              "delete-product.confirm-subtitle",
              "This action cannot be undone."
            )}
          </Typography>
        </Box>
      }
    />
  );
};

export default DeleteProductModal;
